import maplibregl from 'maplibre-gl';

/**
 * Register MapLibre's right-to-left text plugin once, for the whole app.
 *
 * Without it, MapLibre lays out RTL scripts left-to-right, so Hebrew (and
 * Arabic) place labels on the vector basemaps rendered reversed - the bug in
 * issue #75, seen while panning/zooming a map centred on Israel.
 *
 * The plugin is self-hosted at `public/mapbox-gl-rtl-text.js` (vendored from
 * `@mapbox/mapbox-gl-rtl-text@0.3.0`, minified - the build MapLibre 5 expects)
 * rather than pulled from a CDN, so it works under the app's own origin/CSP. It
 * is registered LAZILY (2nd arg): the blob is only fetched the first time the
 * map actually encounters RTL text, so a map that never shows Hebrew pays nothing.
 *
 * `setRTLTextPlugin` may only be called once per page (and not after a map has
 * already parsed text); a second call throws synchronously. A failed lazy load
 * rejects the returned Promise. Both are handled so hot reloads / multiple map
 * instances / an offline plugin file degrade gracefully (Hebrew stays reversed,
 * the map keeps working) rather than crashing.
 */
let requested = false;

export function ensureRtlTextPlugin(): void {
  // No document to render into (SSR / test import) — nothing to register.
  if (requested || typeof window === 'undefined') return;
  requested = true;
  try {
    // lazy=true: fetch the blob only when RTL text is first encountered.
    void maplibregl.setRTLTextPlugin('/mapbox-gl-rtl-text.js', true).catch((err) => {
      // Non-fatal: the map still works, Hebrew labels just stay reversed.
      console.warn('RTL text plugin failed to load; Hebrew labels may render reversed', err);
    });
  } catch (err) {
    // Already registered on this page (second map / hot reload). Harmless.
    console.warn('setRTLTextPlugin skipped (already registered)', err);
  }
}
